import React, { useEffect, useState, useRef } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { money, fileUrl } from "@/lib/api";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DOC_TYPES } from "@/lib/constants";
import { DollarSign, Loader2, Upload, FileText, HardHat } from "lucide-react";
import { toast } from "sonner";

export function ContractorEarnings() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/dashboard/contractor").then(({ data }) => setStats(data)); }, []);
  return (
    <DashboardLayout nav={getNav("contractor")} title="Earnings" bottomNav>
      {!stats ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard testid="earn-pending" label="Pending Payment" value={money(stats.pending_payment)} icon={DollarSign} accent="text-amber-600" sub="Completed jobs awaiting client payment" />
          <StatCard testid="earn-paid" label="Paid Earnings" value={money(stats.paid_earnings)} icon={DollarSign} accent="text-green-600" sub="Total received" />
          <StatCard testid="earn-completed" label="Completed Jobs" value={stats.completed_jobs} icon={FileText} accent="text-emerald-600" />
          <StatCard testid="earn-inprogress" label="In Progress" value={stats.jobs_in_progress} icon={HardHat} accent="text-indigo-600" />
        </div>
      )}
    </DashboardLayout>
  );
}

export function ContractorProfile() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [perf, setPerf] = useState(null);
  const [docType, setDocType] = useState("");
  const [expiry, setExpiry] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => {
    api.get(`/contractors/${user.id}`).then(({ data }) => setDocs(data.documents)).catch(() => {});
    api.get(`/contractors/${user.id}/performance`).then(({ data }) => setPerf(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!docType) { toast.error("Select a document type first"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("doc_type", docType);
    try {
      const { data } = await api.post("/upload/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (expiry) { await api.put(`/documents/${data.id}/expiry`, { expiration_date: expiry }); }
      toast.success("Document uploaded"); setExpiry(""); load();
    } catch (err) { toast.error("Upload failed"); }
    setUploading(false); if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <DashboardLayout nav={getNav("contractor")} title="My Profile" bottomNav>
      <div className="space-y-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold text-lg">{(user.first_name?.[0] || "") + (user.last_name?.[0] || "")}</div>
            <div>
              <h2 className="font-head text-xl font-semibold text-slate-900">{user.first_name} {user.last_name}</h2>
              <p className="text-sm text-slate-500">{user.company_name} · {user.trade}</p>
            </div>
            <span className="ml-auto text-xs font-semibold px-3 py-1 rounded-full bg-blue-100 text-blue-700">{user.application_status}</span>
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Field l="Phone" v={user.phone} /><Field l="Email" v={user.email} /><Field l="City/State" v={`${user.city}, ${user.state}`} />
            <Field l="Service Radius" v={user.service_radius} /><Field l="Experience" v={`${user.years_experience || "—"} yrs`} /><Field l="Crew Size" v={user.crew_size} />
          </div>
        </div>

        {perf && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs uppercase font-bold text-slate-400">Completed</div><div className="font-head text-2xl font-semibold">{perf.completed_jobs}</div></div>
            <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs uppercase font-bold text-slate-400">Accept Rate</div><div className="font-head text-2xl font-semibold">{perf.accept_rate}%</div></div>
            <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs uppercase font-bold text-slate-400">Completion</div><div className="font-head text-2xl font-semibold">{perf.completion_rate}%</div></div>
            <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs uppercase font-bold text-slate-400">Earnings</div><div className="font-head text-2xl font-semibold">{money(perf.total_earnings)}</div></div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-head font-semibold text-slate-900 mb-3">Documents & Credentials</h3>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doc-type-select" className="sm:w-52"><SelectValue placeholder="Document type" /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <input type="date" data-testid="doc-expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="h-10 rounded-lg border border-slate-200 px-3 text-sm sm:w-44" title="Expiration date (optional)" />
            <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="doc-file-input" accept=".pdf,.png,.jpg,.jpeg" />
            <Button data-testid="upload-doc-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="bg-blue-600 hover:bg-blue-700">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Upload</>}
            </Button>
          </div>
          {docs.length === 0 ? <p className="text-sm text-slate-400">No documents uploaded yet.</p> : (
            <div className="space-y-2">
              {docs.map((d) => (
                <a key={d.id} href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div className="flex-1"><div className="font-medium text-sm text-slate-900">{d.doc_type}</div><div className="text-xs text-slate-400">{d.original_filename}</div></div>
                  {d.expiration_date && <span className="text-xs text-slate-500">Expires {d.expiration_date}</span>}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
const Field = ({ l, v }) => <div><div className="text-xs uppercase tracking-wide font-bold text-slate-400">{l}</div><div className="mt-0.5 text-slate-800 truncate">{v || "—"}</div></div>;
